/**
 * DetailPageV2 — the shared v2 tabbed detail-page pattern (Phase 1 fan-out foundation).
 *
 * The recurring detail-page structure from the canonical spec's Candidate detail
 * (design/v2-direction/index.html lines 843-878): an optional back-link, a page
 * HERO region (title + meta + badges + actions, or a full custom hero like the
 * candidate avatar hero), then a shadcn TABS bar whose panels hold SECTION CARDS.
 *
 * Composed of three cooperating pieces so batches can use as much as they need:
 *   • <DetailPageV2>   — the whole scaffold (back-link + header slot + tabs).
 *   • <DetailBackLink> — the canonical `.back-link` chevron affordance.
 *   • <DetailSection>  — a titled section Card (canonical `.card` + `.card-head`)
 *                        used inside tab panels (Scorecard, Reasoning, Skills…).
 *
 * Uses the vendored Tabs primitive (canonical `.tabs`/`.tab` pill idiom) and the
 * vendored Card primitive, so the detail page inherits exact v2 chrome.
 *
 * HOW A BATCH USES THIS
 *   <DetailPageV2
 *     backLink={{ label: "Back to All Candidates", onClick: goList }}
 *     header={<CandidateHero … />}
 *     tabs={[{ value: "eval", label: "AI Evaluation", content: <> … </> }, …]}
 *     defaultTab="eval" />
 *   Inside a tab: wrap each block in <DetailSection title="Scorecard"> … .
 *
 * Density: tokenized primitives + gap-4/gap-5 spacing — under the §2.0 ceiling.
 * Token discipline: no hex / rgb / hsl(var()); semantic utilities only.
 * Renders in light + dark; content caps at the shell's --content-max-w.
 */
import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

/** Canonical `.back-link` — chevron + label, muted, hover to primary. */
export function DetailBackLink({
  label,
  onClick,
  href,
}: {
  label: string;
  onClick?: () => void;
  href?: string;
}): JSX.Element {
  const inner = (
    <>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        width={15}
        height={15}
        aria-hidden
      >
        <path d="m15 18-6-6 6-6" />
      </svg>
      {label}
    </>
  );
  const classes =
    "inline-flex items-center gap-1.5 text-[13px] font-medium text-text-secondary transition-colors hover:text-text-primary";
  if (href) {
    return (
      <a href={href} className={classes}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={classes}>
      {inner}
    </button>
  );
}

/**
 * A titled section Card for use inside a tab panel — canonical `.card` +
 * `.card-head` (title + optional sub) + body. Pass `noBodyPadding` for content
 * that manages its own padding (e.g. a full-bleed table).
 */
export function DetailSection({
  title,
  description,
  action,
  children,
  noBodyPadding = false,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  noBodyPadding?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <Card className={className}>
      {(title || description || action) && (
        <CardHeader className="flex-row items-start justify-between gap-2.5">
          <div className="min-w-0">
            {title && <CardTitle className="text-[15px]">{title}</CardTitle>}
            {description && (
              <CardDescription className="mt-1 text-[13px]">
                {description}
              </CardDescription>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </CardHeader>
      )}
      {noBodyPadding ? (
        children
      ) : (
        <CardContent className={cn(!title && !description && !action && "pt-5")}>
          {children}
        </CardContent>
      )}
    </Card>
  );
}

export interface DetailTab {
  value: string;
  label: ReactNode;
  content: ReactNode;
}

export interface DetailPageV2Props {
  /** Optional back affordance above the header. */
  backLink?: { label: string; onClick?: () => void; href?: string };
  /** The hero/header region — a custom hero node, or title+meta+actions markup. */
  header: ReactNode;
  /** Tab definitions. When omitted, only the header renders. */
  tabs?: DetailTab[];
  /** Controlled active tab value. */
  value?: string;
  /** Uncontrolled default active tab value. */
  defaultTab?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

export function DetailPageV2({
  backLink,
  header,
  tabs,
  value,
  defaultTab,
  onValueChange,
  className,
}: DetailPageV2Props): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-5", className)}>
      {backLink && (
        <DetailBackLink
          label={backLink.label}
          onClick={backLink.onClick}
          href={backLink.href}
        />
      )}

      {/* Hero / page header region (title + meta + badges + actions, or custom). */}
      <div>{header}</div>

      {tabs && tabs.length > 0 && (
        <Tabs
          value={value}
          defaultValue={defaultTab ?? tabs[0]?.value}
          onValueChange={onValueChange}
        >
          <TabsList>
            {tabs.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.value} value={t.value}>
              {/* Section cards inside a panel stack with gap-4. */}
              <div className="flex flex-col gap-4">{t.content}</div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
